import React, { useState } from 'react';
import { ReactComponent as CheckIcon } from 'images/check-icon.svg';

import Expander from 'components/Expander/Expander';
import MoreLink from 'components/MoreLink';
import BottomSheet from 'components/Common/BottomSheet';
import Skeleton from 'components/Common/Skeleton';
import { GoalAction } from 'components/FinancialCheckup/FinancialDashboard/GoalsSection/useGoal';
import { AI_COACH_DISCLAIMER } from 'components/Footer/Disclaimers/Disclaimers';
import { renderDescriptionWithLink } from 'utils/renderDescriptionWithLink';
import { RoutePath } from 'enums/Routes';
import { OFFER_AVAILABLE_FROM_DASHBOARD_STATE } from 'utils/navigationStates';
import DashboardSheetHeader from 'components/FinancialCheckup/FinancialDashboard/DashboardActionTiles/DashboardSheetHeader';
import { DashboardTileIconTone } from 'components/FinancialCheckup/FinancialDashboard/DashboardActionTiles/types';

import styles from './RecommendedActions.module.scss';

interface RecommendedActionsProps {
  actions: GoalAction[];
  isLoading?: boolean;
}

const MAX_VISIBLE_ACTIONS = 1;

const ActionItem: React.FC<{ action: GoalAction }> = ({ action }) => (
  <div className={styles.actionItem}>
    <div className={styles.iconContainer}>
      <CheckIcon className={styles.checkIcon} />
    </div>
    <div className={styles.textContainer}>
      <div className={styles.actionTitle}>{action.title}</div>
      <div className={styles.actionDescription}>
        {renderDescriptionWithLink({
          description: action.description,
          keyword: 'Plannery',
          route: RoutePath.OfferAvailable,
          className: styles.planneryLink,
          state: OFFER_AVAILABLE_FROM_DASHBOARD_STATE.state,
        })}
      </div>
    </div>
  </div>
);

const ActionItemSkeleton: React.FC = () => (
  <div className={styles.actionItem}>
    <Skeleton width="16px" height="16px" borderRadius="4px" />
    <div className={styles.textContainer}>
      <Skeleton width="70%" height="20px" />
      <Skeleton width="100%" height="16px" />
      <Skeleton width="80%" height="16px" />
    </div>
  </div>
);

const ActionsList: React.FC<{ actions: GoalAction[] }> = ({ actions }) => (
  <>
    {actions.map((action) => (
      <ActionItem key={action.title} action={action} />
    ))}
  </>
);

const RecommendedActions: React.FC<RecommendedActionsProps> = ({ actions, isLoading }) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  if (!isLoading && actions.length === 0) return null;

  const visibleActions = actions.slice(0, MAX_VISIBLE_ACTIONS);
  const hasMoreActions = !isLoading && actions.length > MAX_VISIBLE_ACTIONS;
  const sectionLabel = actions.length > 1 ? 'Next steps' : 'Next step';

  return (
    <div className={styles.recommendedActions}>
      <div className={styles.divider} />
      <Expander
        label={<span className={styles.actionsText}>{isLoading ? 'Next steps' : sectionLabel}</span>}
        labelClassname={styles.actionsHeader}
        translateArrow={false}
        isOpenByDefault
        content={
          <div className={styles.actionsContent}>
            {isLoading ? <ActionItemSkeleton /> : <ActionsList actions={visibleActions} />}
            {hasMoreActions && (
              <MoreLink
                text="View more"
                onClick={() => setIsSheetOpen(true)}
                showChevron={false}
                className={styles.viewMoreLink}
              />
            )}
            <div className={styles.separator}>
              <p className={styles.aiDisclaimer}>{AI_COACH_DISCLAIMER}</p>
            </div>
          </div>
        }
      />

      <BottomSheet isOpen={isSheetOpen} onClose={() => setIsSheetOpen(false)}>
        <DashboardSheetHeader
          title="Next steps"
          icon={<CheckIcon className={styles.checkIcon} />}
          iconTone={DashboardTileIconTone.Blue}
        />
        <div className={styles.sheetActions}>
          <ActionsList actions={actions} />
          <div className={styles.separator}>
            <p className={styles.aiDisclaimer}>{AI_COACH_DISCLAIMER}</p>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
};

export default RecommendedActions;
